import React, { useState, useEffect, useRef } from 'react';
import { FriendSummary, FriendshipTier } from '../types/friends';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../../shared/components/ui/ConfirmationModal';
import { EditFriendshipModal } from './EditFriendshipModal';

interface FriendCardProps {
  friend: FriendSummary;
  onUnfriend: (friendId: string) => void;
  onUpdateTier: (friendId: string, tier: FriendshipTier) => Promise<void>;
  onRequestHigherTier: (friendId: string, tier: FriendshipTier, note?: string) => Promise<void>;
  timeWindowLabel: string;
}

const TIER_COLORS: Record<FriendshipTier, string> = {
  'TIER_1': 'text-yellow-500',
  'TIER_2': 'text-gray-400',
  'TIER_3': '', // Custom color #A75217 applied via inline style
  'BASIC': 'text-gray-300'
};

const TIER_LABELS: Record<FriendshipTier, string> = {
  'TIER_1': 'Gold tier',
  'TIER_2': 'Silver tier',
  'TIER_3': 'Bronze tier',
  'BASIC': 'Basic tier'
};

export const FriendCard: React.FC<FriendCardProps> = ({ friend, onUnfriend, onUpdateTier, onRequestHigherTier, timeWindowLabel }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showUnfriendModal, setShowUnfriendModal] = useState(false);
  const [isUnfriending, setIsUnfriending] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Tooltip state for tier star
  const [showTierTooltip, setShowTierTooltip] = useState(false);
  const tierTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const starRef = useRef<HTMLSpanElement>(null);

  // Tooltip state for last refreshed icon
  const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);
  const refreshTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use unread count from friend prop (already fetched from backend)
  const hasUnreadMessages = friend.unreadMessageCount > 0;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle tier tooltip on hover with 0.5s delay
  const handleStarMouseEnter = () => {
    tierTooltipTimeoutRef.current = setTimeout(() => {
      setShowTierTooltip(true);
    }, 500);
  };

  const handleStarMouseLeave = () => {
    if (tierTooltipTimeoutRef.current) {
      clearTimeout(tierTooltipTimeoutRef.current);
      tierTooltipTimeoutRef.current = null;
    }
    setShowTierTooltip(false);
  };

  // Handle refresh icon tooltip on hover with 0.5s delay
  const handleRefreshIconMouseEnter = () => {
    refreshTooltipTimeoutRef.current = setTimeout(() => {
      setShowRefreshTooltip(true);
    }, 500);
  };

  const handleRefreshIconMouseLeave = () => {
    if (refreshTooltipTimeoutRef.current) {
      clearTimeout(refreshTooltipTimeoutRef.current);
      refreshTooltipTimeoutRef.current = null;
    }
    setShowRefreshTooltip(false);
  };

  // Format last portfolio update date
  const formatLastPortfolioUpdate = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      
      // Format date as "1/11/26"
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2);
      const dateStr = `${month}/${day}/${year}`;
      
      // Format time as "4:50pm"
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const hours12 = hours % 12 || 12;
      const minutesStr = minutes.toString().padStart(2, '0');
      const timeStr = `${hours12}:${minutesStr}${ampm}`;
      
      // Get timezone abbreviation (e.g., "MDT", "EST")
      const timeZoneFormatter = new Intl.DateTimeFormat(undefined, {
        timeZoneName: 'short',
      });
      const parts = timeZoneFormatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      const timeZoneStr = timeZonePart?.value || '';
      
      return `${dateStr} ${timeStr} ${timeZoneStr}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (tierTooltipTimeoutRef.current) {
        clearTimeout(tierTooltipTimeoutRef.current);
      }
      if (refreshTooltipTimeoutRef.current) {
        clearTimeout(refreshTooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking buttons/menu
    if ((e.target as HTMLElement).closest('button')) return;
    navigate(`/friends/${friend.friendId}`);
  };

  const handleMessageClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      // Navigate to chat page with friendId query param
      // ChatPage will handle opening the conversation and showing/hiding the list appropriately
      navigate(`/chat?friendId=${friend.friendId}`);
    } catch (err) {
      console.error('Failed to open chat:', err);
    }
  };

  const handleUnfriendClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowUnfriendModal(true);
    setIsMenuOpen(false);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEditModal(true);
    setIsMenuOpen(false);
  };

  const handleConfirmUnfriend = async () => {
    setIsUnfriending(true);
    try {
      await onUnfriend(friend.friendId);
      setShowUnfriendModal(false);
    } catch (error) {
      console.error('Failed to unfriend:', error);
      // Keep modal open on error so user can retry
    } finally {
      setIsUnfriending(false);
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        {/* Top Row: Avatar + Info */}
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex-shrink-0 rounded-full">
            {friend.profilePictureUrl ? (
              <img className="h-14 w-14 rounded-full object-cover" src={friend.profilePictureUrl} alt="" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 font-medium text-xl">
                {friend.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          
          <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-wrap md:flex-nowrap">
              <h3 className="text-base font-semibold text-gray-900 break-words md:truncate">{friend.name}</h3>
              <div 
                className="relative flex-shrink-0"
                onMouseEnter={handleStarMouseEnter}
                onMouseLeave={handleStarMouseLeave}
              >
                <span 
                  ref={starRef}
                  className={`${TIER_COLORS[friend.tierFromFriendToMe] || ''} text-base cursor-pointer`}
                  style={friend.tierFromFriendToMe === 'TIER_3' ? { color: '#A75217' } : undefined}
                >
                  ★
                </span>
                
                {/* Tooltip */}
                {showTierTooltip && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                    {TIER_LABELS[friend.tierFromFriendToMe]}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                      <div className="border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-500 truncate">@{friend.username}</p>
          </div>
        </div>

        {/* Middle: Portfolio Change (desktop only) */}
        {friend.portfolioPercentChange !== null && (
          <div className="flex-shrink-0 mx-6 text-right hidden md:block">
            <div className="flex flex-col items-end">
              {/* Portfolio % and Last Updated Icon on same row */}
              <div className="flex items-center justify-end gap-2">
                <div className="text-lg font-semibold">
                  <span className={friend.portfolioPercentChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {friend.portfolioPercentChange >= 0 ? '+' : ''}{friend.portfolioPercentChange}%
                  </span>
                </div>
                {friend.lastPortfolioUpdate && (
                  <div 
                    className="relative flex-shrink-0"
                    onMouseEnter={handleRefreshIconMouseEnter}
                    onMouseLeave={handleRefreshIconMouseLeave}
                  >
                    <img 
                      src="/icon-last-updated-lrg.png"
                      alt="Last portfolio update"
                      className="w-4 h-4 cursor-pointer"
                    />
                    
                    {/* Tooltip */}
                    {showRefreshTooltip && (
                      <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                        <div>Last portfolio update:</div>
                        <div>{formatLastPortfolioUpdate(friend.lastPortfolioUpdate) || 'Unknown'}</div>
                        <div className="absolute top-full right-4 transform -translate-x-1/2 -mt-1">
                          <div className="border-4 border-transparent border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Time window on its own row below */}
              <div className="text-xs text-gray-400">past {timeWindowLabel}</div>
            </div>
          </div>
        )}

        {/* Right: Actions (desktop) */}
        <div className="flex items-center space-x-3 flex-shrink-0 hidden md:flex">
          <button 
            className="relative inline-flex items-center justify-center p-2 border border-blue-600 rounded-full text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            onClick={handleMessageClick}
            title="Message"
          >
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {hasUnreadMessages && (
              <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"></span>
            )}
          </button>
          
          <div className="relative" ref={menuRef}>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-50"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            {isMenuOpen && (
              <div 
                className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-100"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleEditClick}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={handleUnfriendClick}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  Unfriend
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Bottom Row: Portfolio Change + Actions */}
        <div className="flex items-center justify-end space-x-3 mt-3 pt-3 border-t border-gray-100 md:hidden">
          {/* Portfolio Change */}
          {friend.portfolioPercentChange !== null && (
            <div className="flex items-center justify-end gap-2">
              <div className="text-lg font-semibold">
                <span className={friend.portfolioPercentChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {friend.portfolioPercentChange >= 0 ? '+' : ''}{friend.portfolioPercentChange}%
                </span>
              </div>
              {friend.lastPortfolioUpdate && (
                <div 
                  className="relative flex-shrink-0"
                  onMouseEnter={handleRefreshIconMouseEnter}
                  onMouseLeave={handleRefreshIconMouseLeave}
                >
                  <img 
                    src="/icon-last-updated-lrg.png"
                    alt="Last portfolio update"
                    className="w-4 h-4 cursor-pointer"
                  />
                  
                  {/* Tooltip */}
                  {showRefreshTooltip && (
                    <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                      <div>Last portfolio update:</div>
                      <div>{formatLastPortfolioUpdate(friend.lastPortfolioUpdate) || 'Unknown'}</div>
                      <div className="absolute top-full right-4 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs text-gray-400">past {timeWindowLabel}</div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <button 
              className="relative inline-flex items-center justify-center p-2 border border-blue-600 rounded-full text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              onClick={handleMessageClick}
              title="Message"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {hasUnreadMessages && (
                <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"></span>
              )}
            </button>
            
            <div className="relative" ref={menuRef}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-50"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
              {isMenuOpen && (
                <div 
                  className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={handleEditClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleUnfriendClick}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Unfriend
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Friendship Modal */}
      <EditFriendshipModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onUpdate={async (tier, note) => {
          await onUpdateTier(friend.friendId, tier);
        }}
        onRequestHigher={async (tier, note) => {
          await onRequestHigherTier(friend.friendId, tier, note);
        }}
        friendName={friend.name}
        currentTier={friend.tierFromMeToFriend}
      />

      {/* Unfriend Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnfriendModal}
        onClose={() => {
          if (!isUnfriending) {
            setShowUnfriendModal(false);
          }
        }}
        title="Unfriend"
        message={
          <span>
            Are you sure you want to unfriend <strong>{friend.name}</strong>?
          </span>
        }
        confirmLabel="Unfriend"
        cancelLabel="Cancel"
        onConfirm={handleConfirmUnfriend}
        isLoading={isUnfriending}
      />
    </div>
  );
};

